import Binders from 'fe-design-base/assets/illustrationsDeprecated/Binders';
import BubbleAirplane from 'fe-design-base/assets/illustrationsDeprecated/BubbleAirplane';
import ChalkPlay from 'fe-design-base/assets/illustrationsDeprecated/ChalkPlay';
import FryingPan from 'fe-design-base/assets/illustrationsDeprecated/FryingPan';
import HeyEnvelope from 'fe-design-base/assets/illustrationsDeprecated/HeyEnvelope';
import Map from 'fe-design-base/assets/illustrationsDeprecated/Map';
import MoneyClock from 'fe-design-base/assets/illustrationsDeprecated/MoneyClock';
import Payday from 'fe-design-base/assets/illustrationsDeprecated/Payday';
import SquareClock from 'fe-design-base/assets/illustrationsDeprecated/SquareClock';
import omit from 'lodash/omit';
import snakeCase from 'lodash/snakeCase';

import { isValidTierName, slugForPlanName } from 'features/monetization/util';

import { toI18n } from 'util/i18n';

export const NEW_BUSINESS_KIT_FEATURE_KEYS = ['unlimitedPayroll'];

export const sanitizePaidPlan = (plan: string | null) =>
  (plan && plan !== 'basic' && isValidTierName(plan)
    ? slugForPlanName(plan)
    : 'all-in-one') as FeaturedTiers;

export const translationsForFeature = (key: string) => ({
  title: toI18n(`monetization.focus_view.features.${key}.title`),
  description: toI18n(`monetization.focus_view.features.${key}.description`),
});

const FEATURES_ICONS = {
  basicTimeTrackingAndScheduling: { illustration: SquareClock },
  teamCommunicationAndManagement: { illustration: FryingPan },
  timeOffControlsAndPtoTracking: { illustration: SquareClock },
  laborCostManagement: { illustration: MoneyClock },
  retentionAndPerformanceTracking: { illustration: FryingPan },
  messagingAndMobileApp: { illustration: BubbleAirplane },
  remoteAndFieldTeamTools: { illustration: Map },
  liveSupport: { illustration: FryingPan },
  businessInsights: { illustration: ChalkPlay },
  permissionAndDepartments: { illustration: FryingPan },
  unlimitedPayroll: { illustration: Payday },
  newHireOnboarding: { illustration: HeyEnvelope },
  documentationAndStorage: { illustration: Binders },
  hrProAdvisors: { illustration: FryingPan },
};

export type FeatureType = {
  title: string;
  description: string;
  illustration: React.FC<{ w: number }>;
};

export type FeatureKey = keyof typeof FEATURES_ICONS;

const featuresKeys = Object.keys(FEATURES_ICONS) as FeatureKey[];

const FEATURES = featuresKeys.reduce<Record<FeatureKey, FeatureType>>(
  (acc, feature) => {
    acc[feature] = {
      ...translationsForFeature(snakeCase(feature)),
      ...FEATURES_ICONS[feature],
    };
    return acc;
  },
  {} as Record<FeatureKey, FeatureType>
);

export type FeaturesTypes =
  | typeof ALL_IN_ONE_FEATURES
  | typeof PLUS_FEATURES
  | typeof ESSENTIALS_FEATURES
  | typeof NEW_BUSINESS_KIT_FEATURES;

export type FeaturedTiers =
  | 'essentials'
  | 'plus'
  | 'all-in-one'
  | 'new-business-kit';

export type FeaturesTypesKeys = keyof FeaturesTypes;

const ALL_IN_ONE_FEATURES = omit(FEATURES, 'unlimitedPayroll');

const PLUS_FEATURES = {
  basicTimeTrackingAndScheduling: FEATURES.basicTimeTrackingAndScheduling,
  teamCommunicationAndManagement: FEATURES.teamCommunicationAndManagement,
  timeOffControlsAndPtoTracking: FEATURES.timeOffControlsAndPtoTracking,
  laborCostManagement: FEATURES.laborCostManagement,
  retentionAndPerformanceTracking: FEATURES.retentionAndPerformanceTracking,
  messagingAndMobileApp: FEATURES.messagingAndMobileApp,
  remoteAndFieldTeamTools: FEATURES.remoteAndFieldTeamTools,
  liveSupport: FEATURES.liveSupport,
  businessInsights: FEATURES.businessInsights,
  permissionAndDepartments: FEATURES.permissionAndDepartments,
};

const ESSENTIALS_FEATURES = {
  basicTimeTrackingAndScheduling: FEATURES.basicTimeTrackingAndScheduling,
  teamCommunicationAndManagement: FEATURES.teamCommunicationAndManagement,
  retentionAndPerformanceTracking: FEATURES.retentionAndPerformanceTracking,
  messagingAndMobileApp: FEATURES.messagingAndMobileApp,
  remoteAndFieldTeamTools: FEATURES.remoteAndFieldTeamTools,
  liveSupport: FEATURES.liveSupport,
};

const NEW_BUSINESS_KIT_FEATURES = omit(FEATURES, 'permissionAndDepartments');

export const FEATURES_MAPPER: Record<FeaturedTiers, FeaturesTypes> = {
  'all-in-one': ALL_IN_ONE_FEATURES,
  plus: PLUS_FEATURES,
  essentials: ESSENTIALS_FEATURES,
  'new-business-kit': NEW_BUSINESS_KIT_FEATURES,
};

export type PickedFeatures = Record<string, FeatureType>;

export const pickFeaturesByKeys =
  (features: FeaturesTypes, focusedFeaturesKeys: FeaturesTypesKeys[] | null) =>
  (start: number, end?: number) => {
    const featuresWithoutSelected: PickedFeatures = Object.keys(
      features
    ).reduce((acc: PickedFeatures, feature) => {
      const isFocused =
        focusedFeaturesKeys &&
        focusedFeaturesKeys.includes(feature as FeaturesTypesKeys);
      if (!isFocused) {
        acc[feature] = features[feature as FeaturesTypesKeys];
      }
      return acc;
    }, {});

    return Object.keys(featuresWithoutSelected)
      .slice(start, end)
      .reduce((acc: PickedFeatures, feature) => {
        acc[feature] = featuresWithoutSelected[feature];
        return acc;
      }, {});
  };

export const getFocusedFeaturesKeys = ({
  plan,
  featureKey,
}: {
  plan: unknown;
  featureKey: unknown;
}) => {
  if (!featureKey) return [];
  if (typeof featureKey !== 'string') return [];

  const rawKeys = featureKey.split(',');
  const rawKey = rawKeys[0];

  const singleFocusedFeatureKey = (
    rawKey && featuresKeys.includes(rawKey as FeaturesTypesKeys) ? rawKey : null
  ) as FeaturesTypesKeys | null;

  if (!singleFocusedFeatureKey) {
    switch (plan) {
      case 'all-in-one':
      case 'enterprise':
        return ['newHireOnboarding'];
      case 'new-business-kit':
        return NEW_BUSINESS_KIT_FEATURE_KEYS;
      case 'plus':
        return ['timeOffControlsAndPtoTracking'];
      case 'essentials':
        return ['basicTimeTrackingAndScheduling'];
      default:
        return [];
    }
  }

  return [singleFocusedFeatureKey];
};

export const formatFocusViewPlan = (plan: string | null) => {
  const paidPlan = sanitizePaidPlan(plan);
  if (!paidPlan || paidPlan === 'all-in-one') {
    return 'enterprise';
  }

  return plan;
};
